import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAuthStore } from '../../stores/auth.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon, type IconName } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Pagination } from '../../components/ui/Pagination';
import { Helper } from '../../components/ui/Helper';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { MobileFilters } from '../../components/ui/MobileFilters';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { useUrlState } from '../../hooks/useUrlState';
import { useDocActions } from './useDocActions';
import { useAdminActions } from '../admin/useAdminActions';
import { TrashPopover } from '../admin/TrashPopover';
import { fmtEur, fmtDate } from '../../lib/utils';
import {
  documentsQuery, type DocSource, type DocKind, type InvoiceItem, type QuoteItem, type UnifiedRow,
} from '../../lib/scadenziario';
import { InvoiceType, InvoiceStatus, QuoteStatus, ModuleKey, PermissionLevel, hasAtLeast } from '@masamune/shared';

/**
 * Documenti — la lista di fatture, note di credito e preventivi.
 *
 * Vive nella tab "Documenti" dell'Amministrazione (l'unica sezione dei soldi).
 *   filterbar → pill tipo + menu '···' · cerca · pill stato + menu '···'
 *               · contatore · "Nuovo documento"
 *   tabella   → Tipo (solo su 'Fiscali') · N°/Cliente · Progetto · Emissione
 *               · Scadenza · Totale · Stato · azione contestuale
 *
 * Filtri e paginazione sono SUL SERVER (594 fatture in DB: una lista
 * client-side su /invoices?pageSize=200 ne mostrerebbe solo un pezzo).
 * Ogni filtro = una query sola, quindi il tipo sceglie anche l'endpoint:
 * i preventivi stanno su /quotes, il resto su /invoices.
 *
 * Stato in URL (deep-link / bookmark, dentro /admin?tab=documenti):
 *   type · status · q  — es. /admin?tab=documenti&type=quote&status=INVIATO
 *
 * `showHistory` arriva dall'interruttore "Anni scorsi" dell'Amministrazione:
 * senza, la lista parte dalla data di taglio (OrgSettings.adminCutoffDate).
 */

const PAGE_SIZE = 25;

export const STATUS_INFO: Record<string, { label: string; tone: 'ok' | 'warn' | 'alert' | 'info' | 'ghost' | 'default' }> = {
  BOZZA:        { label: 'Bozza',        tone: 'ghost' },
  EMESSA:       { label: 'Emessa',       tone: 'warn' },
  INVIATA:      { label: 'Inviata',      tone: 'warn' },
  PAGATA:       { label: 'Pagata',       tone: 'ok' },
  SCADUTA:      { label: 'Scaduta',      tone: 'alert' },
  ANNULLATA:    { label: 'Annullata',    tone: 'ghost' },
  INVIATO:      { label: 'Inviato',      tone: 'warn' },
  VISUALIZZATO: { label: 'Visto',        tone: 'warn' },
  ACCETTATO:    { label: 'Firmato',      tone: 'ok' },
  RIFIUTATO:    { label: 'Rifiutato',    tone: 'alert' },
  SCADUTO:      { label: 'Scaduto',      tone: 'ghost' },
  CONVERTITO:   { label: 'Convertito',   tone: 'ok' },
};

interface SourceDef { key: DocSource; label: string; icon: IconName; title: string }

const SOURCES: SourceDef[] = [
  { key: 'all',              label: 'Fiscali',         icon: 'grid',    title: 'Fatture, note di credito, proforme e avvisi' },
  { key: 'FATTURA',          label: 'Fatture',         icon: 'invoice', title: 'Solo le fatture' },
  { key: 'NOTA_CREDITO',     label: 'Note di credito', icon: 'refresh', title: 'Storni di fatture precedenti' },
  { key: 'quote',            label: 'Preventivi',      icon: 'file',    title: 'Proposte commerciali da firmare' },
  { key: 'PROFORMA',         label: 'Proforme',        icon: 'send',    title: 'Pre-fatture non fiscali' },
  { key: 'AVVISO_PAGAMENTO', label: 'Avvisi',          icon: 'bell',    title: 'Avvisi di pagamento' },
];
// Tipi sempre visibili come pill; gli altri vivono nel menu '···' (overflow),
// e quello scelto dal menu viene promosso a pill finché resta attivo.
const MAIN_SOURCE_KEYS: DocSource[] = ['all', 'FATTURA', 'quote'];

/**
 * Stati: il server filtra su UN valore dell'enum, quindi niente gruppi
 * ("aperte" = EMESSA + INVIATA non si può chiedere in una query sola).
 * Le rate davvero da incassare hanno una tab tutta loro: /admin?tab=incassi.
 */
const STATUS_INVOICE = [
  { key: '',                        label: 'Tutti' },
  { key: InvoiceStatus.EMESSA,      label: 'Emesse' },
  { key: InvoiceStatus.SCADUTA,     label: 'Scadute' },
  { key: InvoiceStatus.PAGATA,      label: 'Pagate' },
  { key: InvoiceStatus.BOZZA,       label: 'Bozze' },
  { key: InvoiceStatus.INVIATA,     label: 'Inviate' },
  { key: InvoiceStatus.ANNULLATA,   label: 'Annullate' },
];
const STATUS_QUOTE = [
  { key: '',                        label: 'Tutti' },
  { key: QuoteStatus.INVIATO,       label: 'Inviati' },
  { key: QuoteStatus.ACCETTATO,     label: 'Firmati' },
  { key: QuoteStatus.BOZZA,         label: 'Bozze' },
  { key: QuoteStatus.VISUALIZZATO,  label: 'Visti' },
  { key: QuoteStatus.CONVERTITO,    label: 'Convertiti' },
  { key: QuoteStatus.RIFIUTATO,     label: 'Rifiutati' },
  { key: QuoteStatus.SCADUTO,       label: 'Scaduti' },
];
// Pill di stato mostrate ("Tutti" + la più usata); le altre nel menu '···',
// e quella scelta dal menu viene promossa a pill finché resta attiva.
const STATUS_PILL_COUNT = 2;

interface FicStatus { connected: boolean; companyName: string | null; }

export function DocumentsSection({ showHistory }: { showHistory?: boolean } = {}) {
  const user = useAuthStore((s) => s.user);
  const canWrite = useMemo(() => {
    if (!user) return false;
    const lvl = (user.permissions.find((p) => p.module === ModuleKey.FATTURAZIONE)?.level ?? PermissionLevel.NONE) as PermissionLevel;
    return hasAtLeast(lvl, PermissionLevel.WRITE);
  }, [user]);

  const [typeParam] = useUrlState<string>('type', 'all');
  const source: DocSource = (SOURCES.find((s) => s.key === typeParam) ?? SOURCES[0]!).key;
  const [statusParam] = useUrlState<string>('status', '');
  const statusFilters = source === 'quote' ? STATUS_QUOTE : STATUS_INVOICE;
  // Uno stato di un'altra fonte (es. ?type=quote&status=PAGATA) non esiste qui: torna a "Tutti"
  const status = statusFilters.some((f) => f.key === statusParam) ? statusParam : '';
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const [, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [source, status, search, showHistory]);

  // Aggiornamento atomico di più param (un solo history entry, niente race tra
  // setter consecutivi): cambiando tipo lo stato riparte da "Tutti".
  const setParamsBatch = (next: Record<string, string>) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(next)) {
        if (!v || v === 'all') sp.delete(k);
        else sp.set(k, v);
      }
      return sp;
    });
  };
  const chooseSource = (k: DocSource) => setParamsBatch({ type: k, status: '' });
  const chooseStatus = (k: string) => setParamsBatch({ status: k });

  const params = useMemo(
    () => ({ source, status, search: search.trim(), includeHistory: showHistory, page, pageSize: PAGE_SIZE }),
    [source, status, search, showHistory, page],
  );
  const q = useQuery(documentsQuery(params));
  const { data: ficStatus } = useQuery({
    queryKey: ['fic-status'],
    queryFn: () => api.get<FicStatus>('/settings/integrations/fic/status'),
  });

  const rows = q.data?.rows ?? [];
  const filtered = !!status || !!search.trim();
  const pillSources = SOURCES.filter((s) => MAIN_SOURCE_KEYS.includes(s.key) || s.key === source);
  const overflowSources = SOURCES.filter((s) => !MAIN_SOURCE_KEYS.includes(s.key));
  const pillStatuses = statusFilters.filter((f, i) => i < STATUS_PILL_COUNT || f.key === status);
  const overflowStatuses = statusFilters.slice(STATUS_PILL_COUNT);

  return (
    <>
      {/* Sul telefono i filtri stanno dietro un tasto solo: in fila facevano
          una barra alta 223px che spingeva la lista sotto la piega
          (mobile.md §5). Sul computer MobileFilters restituisce i figli
          così come sono, senza nemmeno un div intorno. */}
      <MobileFilters attivi={[source !== 'all', !!status, !!search].filter(Boolean).length}>
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        {/* Filtro tipo documento — pill principali + menu '···' */}
        <div className="flex items-center gap-1.5" role="group" aria-label="Tipo documento">
          {pillSources.map((s) => (
            <button
              key={s.key}
              onClick={() => chooseSource(s.key)}
              aria-pressed={source === s.key}
              title={s.title}
              className={source === s.key ? 'filter-pill is-active' : 'filter-pill'}
            >
              {s.label}
            </button>
          ))}
          <ActionsMenu
            variant="default"
            label="···"
            excludeKey={source}
            items={overflowSources.map((s) => ({
              key: s.key,
              label: s.label,
              icon: s.icon,
              onClick: () => chooseSource(s.key),
            }))}
          />
        </div>

        <div className="divider" />

        <div className="tb-search flex-1" style={{ minWidth: 200, margin: 0 }}>
          <Icon name="search" size={12} />
          <input
            aria-label="Cerca documenti"
            placeholder={source === 'quote' ? 'Cerca numero, titolo o note…' : 'Cerca numero o note…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Filtro stato — pill principali + menu '···' (stesso pattern del tipo) */}
        <div className="flex items-center gap-1.5" role="group" aria-label="Stato documento">
          {pillStatuses.map((f) => (
            <button
              key={f.key || 'all'}
              onClick={() => chooseStatus(f.key)}
              aria-pressed={status === f.key}
              className={status === f.key ? 'filter-pill is-active' : 'filter-pill'}
            >
              {f.label}
            </button>
          ))}
          <ActionsMenu
            variant="default"
            label="···"
            excludeKey={status}
            items={overflowStatuses.map((f) => ({
              key: f.key,
              label: f.label,
              icon: 'filter' as const,
              onClick: () => chooseStatus(f.key),
            }))}
          />
        </div>

        <span className="mute text-[12px]">{q.data?.total ?? 0} documenti</span>
        {/* Il gemello in fondo allo schermo è la barra d'azione qui sotto:
            in cima il pollice non ci arriva (mobile.md §4). */}
        {canWrite && <span className="d-only"><NewDocumentButton size="sm" /></span>}
      </div>
      </MobileFilters>

      {canWrite && (
        <DesktopOnly
          cosa="La creazione di fatture e preventivi"
          perche="è una procedura a più passaggi con righe, imposte e scadenze; qui puoi consultare e incassare i documenti esistenti."
        >
          <div className="m-actionbar m-only">
            <NewDocumentButton />
          </div>
        </DesktopOnly>
      )}

      {q.isLoading && !q.data ? (
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={8} cols={7} withAvatar={false} /></tbody></table></div>
      ) : rows.length === 0 ? (
        <DocumentsEmpty source={source} filtered={filtered} canWrite={canWrite} onReset={() => setParamsBatch({ status: '', q: '' })} />
      ) : (
        <div className="ed-section">
          {/* tbl--card: su 390 le otto colonne facevano una tabella larga
              1216px dentro 358, che scorreva di lato nascondendo metà dei
              dati (mobile.md §3). Sul computer non cambia niente: tbl--card
              non ha regole fuori dalla media query. */}
          <table className="tbl tbl--ed tbl--card">
            <thead>
              <tr>
                {source === 'all' && <th style={{ width: 90 }}>Tipo</th>}
                <th>N° / Cliente</th>
                <th>Progetto</th>
                <th>Emissione</th>
                <th>
                  Scadenza
                  <Helper text="Per i preventivi è la data oltre cui non sono più impegnativi. Per le fatture è quando il cliente deve pagare." />
                </th>
                <th className="num">Totale</th>
                <th>Stato</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <DocumentRow key={`${r.kind}-${r.id}`} r={r} ficConnected={!!ficStatus?.connected} showType={source === 'all'} canWrite={canWrite} />
              ))}
            </tbody>
          </table>
          {q.data && <Pagination page={q.data.page} pageSize={q.data.pageSize} total={q.data.total} totalPages={q.data.totalPages} onPage={setPage} />}
        </div>
      )}
    </>
  );
}

// Primo-uso reale (nessun documento di quel tipo) vs filtro a vuoto: solo nel
// primo caso ha senso la CTA d'innesco.
function DocumentsEmpty({ source, filtered, canWrite, onReset }: {
  source: DocSource; filtered: boolean; canWrite: boolean; onReset: () => void;
}) {
  const quote = source === 'quote';
  if (filtered) {
    return (
      <EmptyState
        brand
        title="Nessun documento con questi filtri"
        description="Prova col numero del documento, o azzera i filtri per vederli tutti."
        action={<button className="btn" onClick={onReset}>Azzera i filtri</button>}
      />
    );
  }
  return (
    <EmptyState
      brand
      title={quote ? 'Nessun preventivo' : 'Nessun documento'}
      description={quote
        ? 'I preventivi che crei compaiono qui, con lo stato della firma.'
        : 'I documenti emessi compaiono qui, anche quelli che arrivano da FattureInCloud.'}
      action={canWrite
        ? (
          <button className="btn btn-primary d-only" onClick={() => useModalStore.getState().open(quote ? 'newQuote' : 'newInvoice')}>
            <Icon name="plus" size={12} /> {quote ? 'Crea il primo preventivo' : 'Crea il primo documento'}
          </button>
        )
        : undefined}
    />
  );
}

// ════════════════════════════════════════════════════════════════════
// "Nuovo documento" dropdown
// ════════════════════════════════════════════════════════════════════
export function NewDocumentButton({ size = 'md' }: { size?: 'md' | 'sm' }) {
  const [open, setOpen] = useState(false);
  const openModal = useModalStore((s) => s.open);

  const newQuote = () => { setOpen(false); openModal('newQuote'); };
  const newInvoice = (type?: InvoiceType) => { setOpen(false); openModal('newInvoice', type ? { type } : undefined); };

  return (
    <div className="relative">
      <button className={size === 'sm' ? 'btn btn-sm' : 'btn btn-primary'} onClick={() => setOpen((v) => !v)}>
        <Icon name="plus" size={size === 'sm' ? 10 : 14} /> Nuovo documento <Icon name="chevronD" size={10} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 z-[61] rounded-[var(--r-card)] border border-line bg-white shadow-lg overflow-hidden" style={{ minWidth: 340 }}>
            <div className="p-2 border-b border-line">
              <div className="t-micro mb-1.5">Commerciale</div>
              <DocChoice icon="file" title="Preventivo" sub="Proposta firmabile · gratis (eIDAS)" onClick={newQuote} />
            </div>
            <div className="p-2">
              <div className="t-micro mb-1.5">Fiscale</div>
              <DocChoice icon="invoice" title="Fattura"          sub="Documento fiscale numerato"        onClick={() => newInvoice(InvoiceType.FATTURA)} />
              <DocChoice icon="file"    title="Nota di credito"  sub="Storna una fattura precedente"     onClick={() => newInvoice(InvoiceType.NOTA_CREDITO)} />
              <DocChoice icon="send"    title="Proforma"         sub="Pre-fattura non fiscale"           onClick={() => newInvoice(InvoiceType.PROFORMA)} />
              <DocChoice icon="bell"    title="Avviso pagamento" sub="Promemoria per il cliente"         onClick={() => newInvoice(InvoiceType.AVVISO_PAGAMENTO)} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DocChoice({ icon, title, sub, onClick }: { icon: IconName; title: string; sub: string; onClick: () => void }) {
  return (
    <div className="flex items-center gap-2.5 p-2.5 hover:bg-paper-2 cursor-pointer rounded-[var(--r-ctl)] transition" onClick={onClick}>
      <div className="icon-tile sm">
        <Icon name={icon} size={16} />
      </div>
      <div className="flex-1">
        <div className="font-semibold">{title}</div>
        <div className="mute text-[12px]">{sub}</div>
      </div>
      <Icon name="arrowRight" size={12} className="mute" />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Riga documento
// ════════════════════════════════════════════════════════════════════
function DocumentRow({ r, ficConnected, showType, canWrite }: { r: UnifiedRow; ficConnected: boolean; showType: boolean; canWrite: boolean }) {
  const navigate = useNavigate();
  const { issueInvoiceLocal, pushInvoiceFic, markInvoicePaid, convertQuote } = useDocActions();
  const { ignoreDocs } = useAdminActions();
  const [trash, setTrash] = useState(false);

  const info = STATUS_INFO[r.status] ?? { label: r.status, tone: 'default' as const };
  const overdue = r.secondaryDate
    && new Date(r.secondaryDate).getTime() < Date.now()
    && !['PAGATA', 'ACCETTATO', 'CONVERTITO', 'RIFIUTATO', 'SCADUTO', 'ANNULLATA'].includes(r.status);
  const detailPath = r.kind === 'quote' ? `/quotes/${r.id}` : `/invoices/${r.id}`;
  // Preventivo convertito: link diretto alla fattura generata
  const convertedTo = r.kind === 'quote' && r.status === 'CONVERTITO'
    ? (r.raw as QuoteItem).convertedToInvoice
    : null;

  return (
    <tr className="cursor-pointer hover:bg-paper-2" onClick={() => navigate(detailPath)}>
      {showType && (
        <td data-m="key" data-l="Tipo">
          <RowTypeBadge kind={r.kind} invoiceType={r.invoiceType} />
        </td>
      )}
      <td data-m="hd">
        <div className="mono font-semibold">{r.number ?? '—'}</div>
        <div className="text-[12px] mute">{r.client.legalName}</div>
        {convertedTo && (
          <a
            className="mono text-[12px] mute underline cursor-pointer"
            onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${convertedTo.id}`); }}
          >
            fattura {convertedTo.number ?? '—'} →
          </a>
        )}
      </td>
      {/* Progetto ed Emissione si leggono aprendo il documento: in scheda
          restano fuori per non superare le quattro informazioni chiave di
          mobile.md §3. La scadenza invece resta, perché è quella che si
          colora di rosso quando è passata. */}
      <td data-m="off" className="mono mute-2">{r.project?.code ?? '—'}</td>
      <td data-m="off" className="mono mute">{fmtDate(r.primaryDate)}</td>
      <td data-m="key" data-l="Scadenza" className={`mono ${overdue ? 'text-alert' : 'mute'}`}>{fmtDate(r.secondaryDate)}</td>
      <td data-m="key" data-l="Totale" className="num mono font-semibold">€ {fmtEur(r.total)}</td>
      <td data-m="key" data-l="Stato"><Chip tone={info.tone} dot>{info.label}</Chip></td>
      {/* I gesti rapidi (emetti, segna pagata, converti) prendono una riga
          loro: è il motivo per cui questa schermata si apre dal telefono. */}
      <td data-m="row" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-end items-center gap-1 relative">
          <RowActions
            row={r}
            ficConnected={ficConnected}
            onIssue={() => issueInvoiceLocal.mutate(r.id)}
            onPushFic={() => pushInvoiceFic.mutate(r.id)}
            onMarkPaid={() => markInvoicePaid.mutate({ id: r.id, total: Number(r.total) })}
            onConvert={() => convertQuote.mutate({ id: r.id, total: Number(r.total) })}
          />
          {/* Cestina solo sui documenti fiscali: i preventivi non hanno un cestino
              (si eliminano dal loro dettaglio) e non entrano nei conti. */}
          {canWrite && r.kind === 'invoice' && (
            <ActionsMenu items={[{ key: 'trash', label: 'Cestina', icon: 'trash', danger: true, onClick: () => setTrash(true) }]} />
          )}
          {trash && (
            <TrashPopover
              title={r.number ?? '—'}
              pending={ignoreDocs.isPending}
              onClose={() => setTrash(false)}
              onConfirm={(v) => { setTrash(false); ignoreDocs.mutate({ kind: 'invoice', ids: [r.id], label: `${r.number ?? '—'} · ${r.client.legalName}`, ...v }); }}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

// Indice di tipo documento: tessera monocroma (mai un colore per tipo) — il
// colore-accento della riga resta UNO solo, lo Stato. Icona+label preservano il dato.
export function RowTypeBadge({ kind, invoiceType }: { kind: DocKind; invoiceType: InvoiceItem['type'] | null }) {
  let label = '—';
  let icon: IconName = 'file';
  if (kind === 'quote') { label = 'Prev.';  icon = 'file'; }
  else if (invoiceType === 'FATTURA')          { label = 'Fatt.'; icon = 'invoice'; }
  else if (invoiceType === 'NOTA_CREDITO')     { label = 'NC';    icon = 'file'; }
  else if (invoiceType === 'PROFORMA')         { label = 'Prof.'; icon = 'send'; }
  else if (invoiceType === 'AVVISO_PAGAMENTO') { label = 'Avv.';  icon = 'bell'; }
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-6 h-6 grid place-items-center rounded-[var(--r-tile)] bg-paper-2 text-ink">
        <Icon name={icon} size={12} />
      </div>
      <span className="t-micro">{label}</span>
    </div>
  );
}

// Azione di stato contestuale (una sola, con label): Emetti / Incassa / Fattura.
// Niente freccia 'Apri': il click sulla riga naviga già al dettaglio.
function RowActions({
  row, ficConnected, onIssue, onPushFic, onMarkPaid, onConvert,
}: {
  row: UnifiedRow; ficConnected: boolean;
  onIssue: () => void; onPushFic: () => void; onMarkPaid: () => void; onConvert: () => void;
}) {
  if (row.kind === 'invoice' && row.status === 'BOZZA') {
    return (
      <button
        className="btn btn-sm"
        title={ficConnected ? 'Emetti su FattureInCloud' : 'Emetti (locale)'}
        onClick={ficConnected ? onPushFic : onIssue}
      >
        <Icon name="send" size={10} /> Emetti
      </button>
    );
  }
  if (row.kind === 'invoice' && ['EMESSA', 'INVIATA', 'SCADUTA'].includes(row.status)) {
    return (
      <button className="btn btn-sm" title="Segna come pagata" onClick={onMarkPaid}>
        <Icon name="check" size={10} /> Incassa
      </button>
    );
  }
  if (row.kind === 'quote' && row.status === 'ACCETTATO') {
    return (
      <button className="btn btn-sm" title="Converti in fattura" onClick={onConvert}>
        <Icon name="invoice" size={10} /> Fattura
      </button>
    );
  }
  return null;
}
